import { formatOptions } from 'QuoteApp/utils';
import { GetConfigResponseType, ServerResponseType } from './types';

/* Explanation */
// When the transformer is called and the value is being retrieved from cache,
//   the response is already transformed, so this is a TS guard to help ensure,
//   we're transforming if it's a ServerResponseType
function isTransformed(
  value: ServerResponseType | GetConfigResponseType
): value is GetConfigResponseType {
  return (
    value.hasOwnProperty('drivers') &&
    value.hasOwnProperty('vehicles') &&
    value.hasOwnProperty('coverages')
  );
}

export const transformConfigResponse = (
  response: ServerResponseType | GetConfigResponseType
): GetConfigResponseType => {
  if (isTransformed(response)) {
    return response;
  }
  return {
    drivers: {
      allowExcludedDrivers:
        response.insurableParty.allowExcludedDrivers ?? true,
      bypassRemovalQuestions:
        response.insurableParty.bypassRemovalQuestions ?? false,
      coverageHistory: {
        coveredWithinLookbackPeriod: {
          required:
            response.insurableParty.coverageHistory?.coveredWithinLookbackPeriod
              ?.required ?? false,
          options: formatOptions(
            response.insurableParty.coverageHistory?.coveredWithinLookbackPeriod
              ?.options
          ),
        },
      },
      accidents: {
        challengeReasonOptions: formatOptions(
          response.insurableParty.accidents.challengeReasonOptions
        ),
        collectLossInformation:
          response.insurableParty.accidents.collectLossInformation,
        collectAccidentType:
          response.insurableParty.accidents.collectAccidentType,
        lookbackYears: response.insurableParty.accidents.lookbackYears ?? 'few',
        lossOptions: formatOptions(
          response.insurableParty.accidents.lossOptions
        ),
        accidentTypeOptions: formatOptions(
          response.insurableParty.accidents.accidentTypeOptions
        ),
        month: formatOptions(response.insurableParty.accidents.month),
        year: formatOptions(response.insurableParty.accidents.year),
        typeOfLoss: {
          required:
            response.insurableParty.accidents.typeOfLoss?.required ?? false,
          options: formatOptions(
            response.insurableParty.accidents.typeOfLoss?.options
          ),
        },
        showLendingLosses: response.insurableParty.accidents.showLendingLosses,
        assumeAtFault: response.insurableParty.accidents.assumeAtFault,
        collectDayOfAccident:
          response.insurableParty.accidents.collectDayOfAccident,
        driverClaimTypes: formatOptions(
          response.insurableParty.accidents.driverClaimTypes
        ),
        nonListedDriverClaimTypes: formatOptions(
          response.insurableParty.accidents.nonListedDriverClaimTypes
        ),
        collectTypeOfLoss: response.insurableParty.accidents.collectTypeOfLoss,
      },
      currentInsurance: {
        biLevel: formatOptions(
          response.insurableParty.currentInsurance.biLevel
        ),
        carrierName: formatOptions(
          response.insurableParty.currentInsurance.carrierName
        ),
        lapseDuration: formatOptions(
          response.insurableParty.currentInsurance.lapseDuration
        ),
        lapseReason: formatOptions(
          response.insurableParty.currentInsurance.lapseReason
        ),
        insuredDuration: formatOptions(
          response.insurableParty.currentInsurance.insuredDuration
        ),
        askForCurrentPolicy:
          response.insurableParty.currentInsurance.askForCurrentPolicy,
      },
      driverLicense: {
        state: formatOptions(response.insurableParty.driverLicense.state),
        status: formatOptions(response.insurableParty.driverLicense.status),
        verified: {
          required:
            response.insurableParty.driverLicense.verified?.required ?? false,
        },
        hasForeignLicense: {
          required:
            response.insurableParty.driverLicense.hasForeignLicense.required ??
            false,
        },
        hasLearnersPermit: {
          required:
            response.insurableParty.driverLicense.hasLearnersPermit.required ??
            false,
        },
        askForUserReportedStatus:
          response.insurableParty.driverLicense.askForUserReportedStatus ??
          false,
        askForAgeFirstLicensed:
          response.insurableParty.driverLicense.askForAgeFirstLicensed ?? true,
        askForSuspensions:
          response.insurableParty.driverLicense.askForSuspensions ?? true,
        userReportedStatus: formatOptions(
          response.insurableParty.driverLicense.userReportedStatus
        ),
        reportStatus: formatOptions(
          response.insurableParty.driverLicense.reportStatus
        ),
      },
      stateResidency: {
        required: response.insurableParty.stateResidency.required ?? false,
      },
      drivingHistory: {
        previousSuspensionCount: formatOptions(
          response.insurableParty.drivingHistory.previousSuspensionCount
        ),
        previousSuspensionDate: formatOptions(
          response.insurableParty.drivingHistory.previousSuspensionDate
        ),
      },
      education: {
        currentLevel: formatOptions(
          response.insurableParty.education.currentLevel
        ),
        askEducation: response.insurableParty.education.askEducation ?? true,
      },
      exclusionReason: formatOptions(response.insurableParty.exclusionReason),
      maxDrivers: response.insurableParty.maxDrivers ?? '',
      militaryService: {
        militaryRank: formatOptions(
          response.insurableParty.militaryService.militaryRank
        ),
        serviceType: formatOptions(
          response.insurableParty.militaryService.serviceType
        ),
        askMilitaryService:
          response.insurableParty.militaryService.askMilitaryService,
      },
      personalInfo: {
        gender: formatOptions(response.insurableParty.personalInfo.gender),
        maritalStatus: formatOptions(
          response.insurableParty.personalInfo.maritalStatus
        ),
        relationshipToApplicant: formatOptions(
          response.insurableParty.personalInfo.relationshipToApplicant
        ),
        suffix: formatOptions(response.insurableParty.personalInfo.suffix),
        ssn: {
          is_optional:
            response.insurableParty.personalInfo.ssn?.is_optional ?? false,
        },
      },
      profession: formatOptions(response.insurableParty.profession),
      askProfession: response.insurableParty.askProfession ?? true,
      askGenderForExcludedDrivers:
        response.insurableParty.askGenderForExcludedDrivers ?? false,
      violations: {
        lookbackYears:
          response.insurableParty.violations.lookbackYears ?? 'few',
        month: formatOptions(response.insurableParty.violations.month),
        day: formatOptions(response.insurableParty.violations.day),
        violationType: formatOptions(
          response.insurableParty.violations.violationType
        ),
        year: formatOptions(response.insurableParty.violations.year),
        challengeReasonOptions: formatOptions(
          response.insurableParty.violations.challengeReasonOptions
        ),
        askForSr22: response.insurableParty.violations.askForSr22,
      },
      removalReason: formatOptions(response.insurableParty.removalReason),
      goodStudentDiscount: response.insurableParty.goodStudentDiscount,
      homeownerVerification: response.insurableParty.homeownerVerification,
      statusDetails: response.insurableParty.statusDetails,
      cfrs: response.insurableParty.cfrs,
      priorPolicies: response.insurableParty.priorPolicies,
    },
    vehicles: {
      lienHolderAddresses: response.insurableItem.lienholders.reduce(
        (acc, bank) => {
          return {
            ...acc,
            [bank.name.value]: { ...bank.address },
          };
        },
        {}
      ),
      lienHolders: response.insurableItem.lienholders.reduce((acc, bank) => {
        return {
          ...acc,
          [bank.name.value]: bank.name.label,
        };
      }, {}),
      usage: formatOptions(response.insurableItem.usage),
      ownership: {
        askRegisteredToApplicant:
          response.insurableItem.ownership?.askRegisteredToApplicant ?? true,
        ownershipType: formatOptions(
          response.insurableItem.ownership.ownershipType
        ),
      },
      parkingDetails: {
        inStateParkingDuration: {
          required:
            response.insurableItem.parkingDetails?.inStateParkingDuration
              ?.required ?? false,
          options: formatOptions(
            response.insurableItem.parkingDetails?.inStateParkingDuration
              ?.options ?? []
          ),
        },
        parkingLocationReason: {
          required:
            response.insurableItem.parkingDetails?.parkingLocationReason
              ?.required ?? false,
          options: formatOptions(
            response.insurableItem.parkingDetails?.parkingLocationReason
              ?.options ?? []
          ),
        },
      },
      parkingType: {
        required: response.insurableItem.parkingType?.required ?? false,
        options: formatOptions(
          response.insurableItem.parkingType?.options ?? []
        ),
      },
      rideshareUse: {
        required: response.insurableItem.rideshareUse?.required ?? false,
      },
      usageDetails: {
        required: response.insurableItem.usageDetails?.required ?? false,
        usedInMexico: {
          required:
            response.insurableItem.usageDetails?.usedInMexico?.required ??
            false,
        },
      },
      mileage: {
        required: response.insurableItem.mileage?.required ?? true,
      },
      insurableItemCoverages: response.coverages.insurableItem.map(c => ({
        ...c,
        coverageLevels: formatOptions(c.coverageLevels),
      })),
      businessUse: {
        required: response.insurableItem.businessUse?.required ?? false,
      },
      businessUseType: formatOptions(response.insurableItem.businessUseType),
    },
    coverages: {
      insurableItem: response.coverages.insurableItem.map(c => ({
        ...c,
        coverageLevels: formatOptions(c.coverageLevels),
      })),
      insuranceProduct: response.coverages.insuranceProduct.map(c => ({
        ...c,
        coverageLevels: formatOptions(c.coverageLevels),
      })),
    },
    agencyInformation: {
      agencyBundle: {
        carrier: formatOptions(response.agencyInformation.agencyBundle.carrier),
        policyType: formatOptions(
          response.agencyInformation.agencyBundle.policyType
        ),
      },
    },
    effectiveDates: {
      startDate: response.effectiveDates?.startDate ?? undefined,
      endDate: response.effectiveDates?.endDate ?? undefined,
    },
    telematics: {
      enabled: response.telematics.enabled,
      required: response.telematics.required,
      enrollmentWindow: response.telematics.enrollmentWindow,
    },
    flexiblePayments: {
      enabled: response.flexiblePayments.enabled,
      paymentFrequencies: response.flexiblePayments.paymentFrequencies,
    },
    checkoutLink: { enabled: response.checkoutLinkEnabled ?? false },
    accessEnabled: response.accessEnabled,
    predictableProtectionFeeApplicable:
      response.predictableProtectionFeeApplicable ?? false,
    sweepEnabled: response.sweepEnabled ?? false,
  };
};
